import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2, CircleAlert } from 'lucide-react'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { getInitials } from 'components/Chat/helper'
import { IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import colors from 'colors'
import { isValidEmail } from '../../helpers'

interface IGuestManagementProps {
  // Search state
  guestSearchQuery: string
  onSearchQueryChange: (query: string) => void
  showDropdown: boolean
  onShowDropdownChange: (show: boolean) => void
  emailError: string
  onEmailErrorChange: (error: string) => void
  guestError: string

  // Filtered users
  filteredUsers: IAssigneeOptions[]
  highlightedIndex: number
  onHighlightedIndexChange: (index: number) => void

  // Selected guests
  selectedGuests: IAssigneeOptions[]
  onGuestSelect: (guest: IAssigneeOptions) => void
  onGuestRemove: (guestValue: string) => void
  onAddGuestByEmail: () => void

  // Keyboard handling
  onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void

  // Refs
  guestItemRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>

  // Current user and all attendees
  currentUserId: string
  inviteeId?: string // Optional: The organizer ID (for edit mode)
  allAttendees: IAssigneeOptions[]

  // All assignee options for checking if email matches internal user
  assigneeOptions?: IAssigneeOptions[]
}

export const GuestManagement: React.FC<IGuestManagementProps> = ({
  guestSearchQuery,
  onSearchQueryChange,
  showDropdown,
  onShowDropdownChange,
  emailError,
  onEmailErrorChange,
  guestError,
  filteredUsers,
  highlightedIndex,
  onHighlightedIndexChange,
  selectedGuests,
  onGuestSelect,
  onGuestRemove,
  onAddGuestByEmail,
  onKeyPress,
  guestItemRefs,
  currentUserId,
  inviteeId,
  allAttendees,
  assigneeOptions,
}) => {
  const { t } = useTranslation()
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Check if the typed email matches an internal user (for showing correct button text)
  const getMatchingInternalUser = () => {
    if (!assigneeOptions || !guestSearchQuery) return null
    const email = guestSearchQuery.trim().toLowerCase()
    return assigneeOptions.find(
      (user) => user.email?.toLowerCase() === email || user.user?.email?.toLowerCase() === email
    )
  }

  const matchingInternalUser = getMatchingInternalUser()

  return (
    <>
      {/* Add People */}
      <div className="mb-5">
        <label className="block text-sm font-semibold text-primary-text mb-2">
          {t('calendar.modal.addPeople')}
        </label>

        <div className="relative" ref={dropdownRef}>
          <div
            className={`flex items-center border rounded-lg px-3 py-2 transition-colors shadow ${
              emailError || guestError ? 'border-alert' : 'border-primary-outline'
            }`}
            style={emailError || guestError ? { borderColor: colors.alert.DEFAULT } : {}}
          >
            <svg
              className="w-4 h-4 text-silver-light mr-2 opacity-70"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
              />
            </svg>
            <input
              type="text"
              placeholder={t('calendar.modal.searchUsersOrEmail')}
              className="flex-1 text-sm outline-none bg-transparent"
              value={guestSearchQuery}
              onChange={(e) => {
                onSearchQueryChange(e.target.value)
                onShowDropdownChange(true)
                onEmailErrorChange('')
                onHighlightedIndexChange(-1)
              }}
              onFocus={() => onShowDropdownChange(true)}
              onKeyDown={onKeyPress}
            />
          </div>
          {/* Email validation error */}
          {emailError && (
            <p
              className="text-xs mt-1.5 flex items-center gap-1 font-medium"
              style={{ color: colors.alert.DEFAULT }}
            >
              <CircleAlert className="w-3.5 h-3.5 flex-shrink-0" color={colors.alert.DEFAULT} />
              {emailError}
            </p>
          )}{' '}
          {/* Guest validation error */}
          {guestError && (
            <p
              className="text-xs mt-1.5 flex items-center gap-1 font-medium"
              style={{ color: colors.alert.DEFAULT }}
            >
              <CircleAlert className="w-3.5 h-3.5 flex-shrink-0" color={colors.alert.DEFAULT} />
              {guestError}
            </p>
          )}{' '}
          {/* Guest Dropdown */}
          {showDropdown && guestSearchQuery && filteredUsers.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-primary-outline ring-1 ring-black/5 rounded-lg shadow-xl z-50 max-h-44 overflow-y-auto">
              {filteredUsers.map((user, index) => (
                <button
                  key={user.value}
                  ref={(el) => (guestItemRefs.current[index] = el)}
                  onClick={() => onGuestSelect(user)}
                  onMouseEnter={() => onHighlightedIndexChange(index)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                    highlightedIndex === index
                      ? 'bg-primary-brandLight'
                      : 'hover:bg-primary-brandLight'
                  }`}
                >
                  <AppAvatar
                    size="sm"
                    type="mixed"
                    shape="Rounded"
                    text={getInitials({
                      firstName: user.user?.firstName || '',
                      lastName: user.user?.lastName || '',
                    })}
                    colorSeed={user.label || user.value}
                    darker
                  />
                  <span className="text-sm text-primary-text">{user.label}</span>
                </button>
              ))}
            </div>
          )}
          {/* Add guest button - only show when email is valid and no users found in dropdown */}
          {showDropdown &&
            guestSearchQuery &&
            filteredUsers.length === 0 &&
            isValidEmail(guestSearchQuery.trim()) && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-primary-outline ring-1 ring-black/5 rounded-lg shadow-xl z-50 px-3 py-3">
                <button
                  onClick={onAddGuestByEmail}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left bg-primary-brandLight hover:bg-primary-brandLight/80 rounded transition-colors"
                >
                  {matchingInternalUser ? (
                    // Internal user found - show avatar with initials
                    <AppAvatar
                      size="sm"
                      type="mixed"
                      shape="Rounded"
                      text={getInitials({
                        firstName: matchingInternalUser.user?.firstName || '',
                        lastName: matchingInternalUser.user?.lastName || '',
                      })}
                      colorSeed={matchingInternalUser.label || matchingInternalUser.value}
                      darker
                    />
                  ) : (
                    // External guest - show email icon
                    <svg
                      className="w-4 h-4 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-primary-text font-medium">
                      {matchingInternalUser
                        ? `Add ${matchingInternalUser.label}`
                        : 'Add external guest'}
                    </p>
                    <p className="text-xs text-silver-light break-all">{guestSearchQuery.trim()}</p>
                  </div>
                </button>
              </div>
            )}
        </div>
      </div>

      {/* Meeting Attendees - only show when there are guests besides organizer */}
      {selectedGuests.length > 0 && (
        <div className="mb-5">
          <label className="block text-sm font-medium text-primary-text mb-2">
            {t('calendar.viewMeeting.meetingAttendees')}
          </label>
          <div className="space-y-1.5">
            {allAttendees.map((attendee) => {
              if (!attendee?.value) return null

              // Use inviteeId if provided (edit mode), otherwise fall back to currentUserId (create mode)
              const organizerId = inviteeId || currentUserId
              const isOrganizer = attendee.value === organizerId
              const isCurrentUser = attendee.value === currentUserId
              const isExternalGuest = attendee.value.includes('@')

              // Get the role label - show "You" for current user unless they're the organizer
              const getRoleBadge = () => {
                if (isOrganizer) return t('calendar.viewMeeting.roleOrganizer')
                if (isCurrentUser) return t('calendar.viewMeeting.roleYou')
                if (isExternalGuest) return '' // Show nothing for external guests
                if (attendee.isAdvisor) return t('calendar.viewMeeting.roleAdvisor')
                return ''
              }

              const roleBadge = getRoleBadge()

              return (
                <div
                  key={attendee.value}
                  className="flex items-center justify-between px-2 py-1.5 hover:bg-gray-50 rounded-md transition-colors group"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isExternalGuest ? (
                      <div className="w-7 h-7 rounded-full bg-primary-brandLight flex items-center justify-center flex-shrink-0">
                        <svg
                          className="w-3.5 h-3.5 text-primary"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                    ) : (
                      <AppAvatar
                        size="sm"
                        type="mixed"
                        shape="Rounded"
                        text={getInitials({
                          firstName: attendee.user?.firstName || '',
                          lastName: attendee.user?.lastName || '',
                        })}
                        colorSeed={attendee.label || attendee.value}
                        darker
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-primary-text truncate">
                        {attendee.label}
                        {roleBadge && (
                          <span className="text-silver-light font-normal text-xs">{roleBadge}</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Delete button - only show for guests, not organizer */}
                  {!isOrganizer && (
                    <button
                      onClick={() => onGuestRemove(attendee.value)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 text-alert hover:bg-alert-light rounded transition-all"
                      title="Remove guest"
                    >
                      <Trash2
                        className="w-3.5 h-3.5"
                        color={colors.primary.error}
                        strokeWidth={1.25}
                        absoluteStrokeWidth
                      />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}
